import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../context/AuthContext'
import InvoiceCard from '../components/InvoiceCard'
import StatCard from '../components/StatCard'
import Card from '../components/Card'
import type { InvoiceRow, ReminderStepRow } from '../types'
import { formatCurrency, formatDate } from '../utils/helpers'

interface InvoiceWithSteps extends InvoiceRow {
  reminder_steps: ReminderStepRow[]
}

interface Summary {
  pending_count: number
  overdue_count: number
  total_unpaid: number
  next_reminder_date: string | null
}

interface Usage {
  active_invoices: number
  active_invoices_limit: number
  scheduled_reminders: number
  scheduled_reminders_limit: number
}

const paymentUrl = import.meta.env.VITE_ICA_DODO_PAYMENT_LINK_URL

export default function Dashboard() {
  const { profile } = useAuth()
  const api = useApi()
  const [invoices, setInvoices] = useState<InvoiceWithSteps[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [usage, setUsage] = useState<Usage | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .getInvoices()
      .then((data) => {
        setInvoices(data.invoices)
        setSummary(data.summary)
        setUsage(data.usage)
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [])

  const isFree = profile?.plan === 'free' || profile?.payment_status === 'inactive'
  const atInvoiceLimit = usage ? usage.active_invoices >= usage.active_invoices_limit : false

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-ica-border border-t-ica-accent" />
      </div>
    )
  }

  if (error) {
    return (
      <Card className="border-ica-red/20 bg-ica-red/5 p-4 text-sm text-ica-red">
        {error}
      </Card>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <Link
          to={atInvoiceLimit ? '/' : '/invoices/new'}
          className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 ${
            atInvoiceLimit
              ? 'bg-ica-amber/15 text-ica-amber'
              : 'bg-ica-accent text-ica-accent-text'
          }`}
        >
          {atInvoiceLimit ? 'Upgrade to add more' : 'New invoice'}
        </Link>
      </div>

      {/* Plan banner */}
      {isFree && (
        <Card className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <p className="text-sm font-medium">You are on the free plan</p>
            <p className="text-xs text-ica-text-muted">Upgrade to beta to send automated reminders.</p>
          </div>
          {paymentUrl ? (
            <a
              href={paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-xl bg-ica-accent px-4 py-2 text-xs font-semibold text-ica-accent-text transition-opacity hover:opacity-90"
            >
              Upgrade
            </a>
          ) : (
            <span className="text-xs text-ica-text-muted">Upgrade (not configured)</span>
          )}
        </Card>
      )}

      {!isFree && (
        <Card className="border-ica-green/20 bg-ica-green/5">
          <p className="text-sm font-medium text-ica-green">
            Plan: {profile?.plan} · Status: {profile?.payment_status}
          </p>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard
          label="Pending"
          value={summary?.pending_count ?? 0}
          delta={`${usage?.active_invoices ?? 0} active`}
        />
        <StatCard
          label="Overdue"
          value={summary?.overdue_count ?? 0}
          delta={summary?.overdue_count ? `${summary.overdue_count} need attention` : 'All clear'}
          deltaPositive={!summary?.overdue_count}
        />
        <StatCard
          label="Unpaid total"
          value={formatCurrency(summary?.total_unpaid ?? 0, 'USD')}
        />
        <StatCard
          label="Next reminder"
          value={summary?.next_reminder_date ? formatDate(summary.next_reminder_date) : '—'}
        />
      </div>

      {/* Usage bars */}
      {usage && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-ica-text-secondary">Active invoices</p>
              <p className="text-sm font-bold">
                {usage.active_invoices} / {usage.active_invoices_limit}
              </p>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-ica-elevated">
              <div
                className="h-full rounded-full bg-ica-accent transition-all"
                style={{
                  width: `${Math.min(100, (usage.active_invoices / usage.active_invoices_limit) * 100)}%`,
                }}
              />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-ica-text-secondary">Scheduled reminders</p>
              <p className="text-sm font-bold">
                {usage.scheduled_reminders} / {usage.scheduled_reminders_limit}
              </p>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-ica-elevated">
              <div
                className="h-full rounded-full bg-ica-accent transition-all"
                style={{
                  width: `${Math.min(100, (usage.scheduled_reminders / usage.scheduled_reminders_limit) * 100)}%`,
                }}
              />
            </div>
          </Card>
        </div>
      )}

      {/* Invoices */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Invoices</h2>
        {invoices.length === 0 ? (
          <Card className="py-12 text-center">
            <p className="text-sm text-ica-text-muted">No invoices yet.{" "}<Link to="/invoices/new" className="text-ica-accent hover:underline">Create one</Link></p>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {invoices.map((invoice) => (
              <InvoiceCard key={invoice.id} invoice={invoice} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
